/**
 * 分 → 元字符串。不含货币符号，不做元→分解析。
 * grouping 默认 false，与 foodbuy/tickets/模板的 toFixed 形状对齐；
 * 千分位由调用方显式打开（crm / market）。
 */
export function centsToYuanString(cents: number, options?: { grouping?: boolean }): string {
  return new Intl.NumberFormat('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: !!options?.grouping
  }).format(cents / 100)
}

const FULLWIDTH_ZERO = '０'.charCodeAt(0)
const ASCII_ZERO = '0'.charCodeAt(0)

/** 全角数字 / 全角小数点收成半角，再交给解析。 */
function normalizeYuanInput(raw: string): string {
  let out = ''
  for (const ch of raw) {
    const code = ch.charCodeAt(0)
    if (code >= FULLWIDTH_ZERO && code <= FULLWIDTH_ZERO + 9) {
      out += String.fromCharCode(ASCII_ZERO + (code - FULLWIDTH_ZERO))
    } else if (ch === '．') {
      out += '.'
    } else {
      out += ch
    }
  }
  return out
}

type YuanParseFailureReason =
  | 'empty'
  | 'scientific-notation'
  | 'too-many-fraction-digits'
  | 'invalid-format'
  | 'out-of-range'

type YuanParseResult =
  | { cents: number; reason: null; message: null }
  | { cents: null; reason: YuanParseFailureReason; message: string }

/**
 * 元字符串 → 分。拒绝科学计数法与超过两位小数（分是最小单位，第三位无法表示）。
 * 不在这里做「必须大于零」——那是业务校验，留给调用方。
 */
export function parseYuanToCents(raw: string): YuanParseResult {
  const normalized = normalizeYuanInput(raw).trim()
  if (!normalized) return { cents: null, reason: 'empty', message: '请输入金额' }
  if (/[eE]/.test(normalized)) {
    return {
      cents: null,
      reason: 'scientific-notation',
      message: '不接受科学计数法，请输入普通小数（例如 100000 而不是 1e5）'
    }
  }
  const match = /^(0|[1-9]\d*)(?:\.(\d+))?$/.exec(normalized)
  if (!match) {
    return { cents: null, reason: 'invalid-format', message: '请输入有效金额（非负、最多两位小数）' }
  }
  const fraction = match[2] ?? ''
  if (fraction.length > 2) {
    return {
      cents: null,
      reason: 'too-many-fraction-digits',
      message: '最多两位小数（金额以分为最小单位，第三位无法表示）'
    }
  }
  const cents = BigInt(match[1]!) * 100n + BigInt(fraction.padEnd(2, '0'))
  if (cents > BigInt(Number.MAX_SAFE_INTEGER)) {
    return { cents: null, reason: 'out-of-range', message: '金额超出系统可安全处理的范围' }
  }
  return { cents: Number(cents), reason: null, message: null }
}

/** 分 → 元输入框值；不用浮点除法，避免大额边界产生尾差。 */
export function centsToYuanInput(cents: number): string {
  if (!Number.isSafeInteger(cents)) throw new Error('金额必须是安全整数分')
  const sign = cents < 0 ? '-' : ''
  const abs = Math.abs(cents)
  const raw = String(abs).padStart(3, '0')
  const whole = raw.slice(0, -2).replace(/^0+(?=\d)/u, '') || '0'
  const fraction = raw.slice(-2)
  return `${sign}${fraction === '00' ? whole : `${whole}.${fraction.replace(/0$/u, '')}`}`
}
